import React, { useState, useEffect } from 'react';
import styles from './styles.module.scss';
import cN from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@Redux/search/store';
import { I_SearchPriceData, I_SearchFilter } from '@Redux/search/interfaces';
import { setShowData, setFilterData } from '@Redux/search/actions';
import fetchAPI from '../../../utils/fetchAPI';
import domainByEnv from '@utils/domainByEnv';
import { I_FilterItemList } from './interface';
import {
    initializeFilterItems,
    updateFilterItems,
    getUpdatedFilterItems,
    getMultiSelectItem,
    filterSearchResult,
} from './utils';

import Skeleton from '@features/skeletons';
import FilterContents from './subComponents/FilterContents';
import SlidePage from '@features/ui/SlidePage';
import SlideFooter from '@features/ui/SlideFooter';

interface I_FilterProps {
    open: boolean;
    whenClose: (a: boolean) => void;
}

const Filters = (props: I_FilterProps) => {
    const moduleName = 'filters';
    const { open, whenClose } = props;
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });

    const {
        SearchPrice, // API回來的原始搜尋結果
        FilterData, // 篩選的資料
    } = useSelector((state: RootState) => state.search);
    const dispatch = useDispatch();

    const [initialFilterItemData, setInitialFilterItemData] =
        useState<I_FilterItemList | null>(null);
    // Mobile 的篩選暫存
    const [mobileTempFilterData, setMobileTempFilterData] =
        useState<I_FilterItemList | null>(FilterData);
    const [showFilterItems, setShowFilterItems] = useState<I_FilterItemList | null>(null);

    const deviceFilter: I_SearchFilter = isMobile ? mobileTempFilterData : FilterData;

    // 從 API 取得初始篩選資料 function
    useEffect(() => {
        const init = async () => {
            const [transactionkey, searchkey] = [
                SearchPrice.data.TransactionKey,
                SearchPrice.data.SearchKey,
            ];
            const filterData = await fetchAPI({
                url:
                    domainByEnv() +
                    `/search/getflightsearchinfofilter?transactionkey=${transactionkey}&searchkey=${searchkey}`,
                req: {},
            });

            const initialFilterData = initializeFilterItems(filterData);

            setInitialFilterItemData(initialFilterData);
            setShowFilterItems(initialFilterData);
        };

        init();
    }, [SearchPrice]);

    // 比對 disable 結果
    useEffect(() => {
        if (!deviceFilter) return setShowFilterItems(initialFilterItemData);

        // 篩選條件後的顯示結果
        const filteredData = filterSearchResult(
            deviceFilter,
            SearchPrice.data
        ) as I_SearchPriceData;

        // 拿新的顯示結果計算篩選欄位 (這邊計算多選欄位的 code 可以註解掉，此處不做篩選)
        const newShowDataFilter = updateFilterItems(filteredData);

        // 方法一 - 取得多選的欄位 (多選之間會交集)
        newShowDataFilter.transfer = getMultiSelectItem(deviceFilter, SearchPrice.data, 'transfer');
        newShowDataFilter.airport = getMultiSelectItem(deviceFilter, SearchPrice.data, 'airport');

        // 方法二 - 取得多選的欄位 (多選之間無交集)
        // const multiSelect = ['transfer', 'airport'];
        // const { transfer: newTransfer, airport: newAirport } = getMultiSelectItems(
        //     deviceFilter,
        //     SearchPrice.data,
        //     multiSelect
        // );
        // newShowDataFilter.transfer = newTransfer;
        // newShowDataFilter.airport = newAirport;

        if (!initialFilterItemData || Object.keys(initialFilterItemData).length === 0) return;
        if (!newShowDataFilter || Object.keys(newShowDataFilter).length === 0) return;

        // 進行比對 disable
        const updatedFilterItems = getUpdatedFilterItems(
            initialFilterItemData,
            newShowDataFilter,
            deviceFilter
        );

        setShowFilterItems(updatedFilterItems);
    }, [mobileTempFilterData, FilterData]);

    // 更新篩選結果
    useEffect(() => {
        if (!SearchPrice.data) return;

        if (FilterData) {
            const showResult = filterSearchResult(FilterData, SearchPrice.data);
            dispatch(setShowData(showResult));
        } else {
            dispatch(setShowData(SearchPrice.data));
        }
    }, [FilterData]);

    // Filter Skeleton
    const filterSkeleton = (
        <div className={cN('search_filtersWrap')}>
            <Skeleton part="filter" />
        </div>
    );

    const mobileProps = {
        tempMFilter: mobileTempFilterData,
        setTempMFilter: setMobileTempFilterData,
    };

    // PC版篩選
    const renderPC = (() => {
        if (!showFilterItems) return filterSkeleton;
        return (
            <div className={cN('search_filtersWrap')}>
                <FilterContents showFilterItems={showFilterItems} />
            </div>
        );
    })();

    // 手機版篩選
    const renderM = (() => {
        if (!showFilterItems) return <div className={cN('search_filtersWrap')} />;

        return (
            <div className={cN('search_filtersWrap')}>
                <div className={styles[`${moduleName}_M`]}>
                    <SlidePage
                        title="篩選"
                        visible={open}
                        setIsVisible={() => {
                            // setSlideOpen(false);
                            whenClose(false);
                            setMobileTempFilterData(FilterData); // 欄位勾選設回正被篩選的條件
                        }}
                    >
                        <div className={styles.SlideContent}>
                            <FilterContents
                                showFilterItems={showFilterItems}
                                mobileProps={mobileProps}
                            />
                            <SlideFooter
                                leftBtnText="重設條件"
                                rightBtnText="確定"
                                onClickLeftBtn={() => {
                                    setMobileTempFilterData(null);
                                    console.log('重設條件');
                                }}
                                onClickRightBtn={() => {
                                    dispatch(setFilterData(mobileTempFilterData));
                                    whenClose(false);
                                    console.log('確定');
                                }}
                            />
                        </div>
                    </SlidePage>
                </div>
            </div>
        );
    })();

    return isMobile ? renderM : renderPC;
};

export default Filters;
